import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/error';
import { allocateMachineRequest } from '@/services/machines';
import { listSitesRequest } from '@/services/sites';
import type { AllocateMachineBody } from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';
import type { SiteListItem } from '@/types/sites';

/**
 * Plan 11-02 — AllocateMachineDialog
 *
 * Radix AlertDialog wrapping a tiny form: pick site (active sites only,
 * excluding the current one), allocation date (defaults today), notes
 * (max 500). Submit calls `allocateMachineRequest(machineId, body)` —
 * 204 closes the dialog and fires `onSuccess` (parent invalidates queries).
 *
 * 400 with field errors (e.g., same-site rejection) surface inline via
 * `amd-error`.
 */

export interface AllocateMachineDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly machineId: number;
  readonly currentSiteId: number | null;
  readonly onSuccess: () => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AllocateMachineDialog({
  open,
  onOpenChange,
  machineId,
  currentSiteId,
  onSuccess,
}: AllocateMachineDialogProps): JSX.Element {
  const [siteId, setSiteId] = useState<number | null>(null);
  const [allocatedAt, setAllocatedAt] = useState<string>(todayIso());
  const [notes, setNotes] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);

  // Reset state on open transition.
  useEffect(() => {
    if (open) {
      setSiteId(null);
      setAllocatedAt(todayIso());
      setNotes('');
      setErrorMessage(null);
      setSubmitAttempted(false);
    }
  }, [open]);

  const sitesQuery = useQuery<PaginatedResult<SiteListItem>>({
    queryKey: ['sites', 'active', 'forAllocateDialog'],
    queryFn: () => listSitesRequest({ status: 'active', page: 1, pageSize: 100 }),
    enabled: open,
  });

  const allocateMutation = useMutation<undefined, ApiError, { body: AllocateMachineBody }>({
    mutationFn: async ({ body }) => {
      await allocateMachineRequest(machineId, body);
      return undefined;
    },
    onSuccess: () => {
      onSuccess();
      onOpenChange(false);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
    },
  });

  function handleSubmit(): void {
    setSubmitAttempted(true);
    if (siteId === null) {
      setErrorMessage('Pick a site.');
      return;
    }
    if (allocatedAt === '' || Number.isNaN(Date.parse(allocatedAt))) {
      setErrorMessage('Pick a valid allocation date.');
      return;
    }
    if (notes.length > 500) {
      setErrorMessage('Notes must be 500 characters or fewer.');
      return;
    }
    setErrorMessage(null);
    const body: AllocateMachineBody = {
      siteId,
      allocatedAt: new Date(`${allocatedAt}T00:00:00Z`).toISOString(),
      notes: notes.trim() === '' ? null : notes.trim(),
    };
    allocateMutation.mutate({ body });
  }

  const siteOptions = (sitesQuery.data?.items ?? []).filter((s) => s.id !== currentSiteId);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="allocate-machine-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Allocate machine to site</AlertDialogTitle>
          <AlertDialogDescription>
            Closing the prior open allocation (if any) and opening a new one to the chosen site.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="amd-site">Site</Label>
            <Select
              value={siteId !== null ? String(siteId) : ''}
              onValueChange={(v) => {
                const n = Number(v);
                if (Number.isFinite(n) && n > 0) setSiteId(n);
              }}
              disabled={sitesQuery.isLoading || allocateMutation.isPending}
            >
              <SelectTrigger id="amd-site" data-testid="amd-site-trigger">
                <SelectValue placeholder="Select a site…" />
              </SelectTrigger>
              <SelectContent>
                {siteOptions.map((s) => (
                  <SelectItem
                    key={s.id}
                    value={String(s.id)}
                    data-testid={`amd-site-option-${String(s.id)}`}
                  >
                    {s.name} ({s.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {submitAttempted && siteId === null && (
              <p className="text-xs text-destructive" data-testid="amd-site-error">
                Pick a site.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="amd-allocated-at">Allocation date</Label>
            <Input
              id="amd-allocated-at"
              type="date"
              value={allocatedAt}
              onChange={(e) => {
                setAllocatedAt(e.target.value);
              }}
              disabled={allocateMutation.isPending}
              data-testid="amd-allocated-at"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="amd-notes">Notes (optional, max 500 chars)</Label>
            <Textarea
              id="amd-notes"
              rows={3}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              disabled={allocateMutation.isPending}
              maxLength={500}
              data-testid="amd-notes"
            />
          </div>
        </div>

        {errorMessage != null && errorMessage !== '' && (
          <Alert variant="destructive" data-testid="amd-error">
            <AlertTitle>Could not allocate</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={allocateMutation.isPending}
            data-testid="amd-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={allocateMutation.isPending}
            data-testid="amd-submit"
          >
            {allocateMutation.isPending ? 'Allocating…' : 'Allocate'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
